@layer components {
  :host {
    /* Size defaults (md). A removable tag is always ≥24px tall (target-size).
       The three public knobs are folded into the private vars here rather than
       at each use site, so an override also carries to what derives from them —
       the toggle's negative inline margins and the × button's spacing math. The
       trio mirrors `.l-button` (`--height` / `--padding-inline` / `--font-size`)
       so a chip can be tuned to a dense filter panel without ::part(base). */
    --_height: var(--height, 1.5rem);
    --_font-size: var(--font-size, var(--l-text-xs));
    --_padding-inline: var(--padding-inline, 0.5rem);
    --_gap: 0.25rem;

    /* Skin, resolved once so `[selected]` can swap all three at the source
       instead of re-declaring the properties that consume them. */
    --_background: var(--background-color, var(--l-color-bg-fill-neutral-subtle));
    --_color: var(--text-color, var(--l-color-text-neutral));
    --_border-strength: 14%;
    /* By default the line is a tint of the ink, so a chip stays coherent under
       any `--text-color` with one declaration. `--border-color` replaces that
       derivation outright rather than shifting it: a consumer with a token
       system wants *its* line token, not a 14% mix of its text color — and
       wants it to hold in every state, which is why it is the fallback in both
       of the pair below rather than a value the hover rule mixes further.
       `--_border-strength` stays private. Selecting a chip swaps the pair
       wholesale, the same way it swaps the fill and the ink. */
    --_border-color: var(
      --border-color,
      color-mix(in oklab, currentColor var(--_border-strength), transparent)
    );
    --_border-color-hover: var(
      --border-color,
      color-mix(in oklab, currentColor calc(var(--_border-strength) + 12%), transparent)
    );
    /* Resolved here rather than at `.base`, because the × segment has to round
       its outer end to the same curve. */
    --_border-radius: var(--border-radius, var(--l-radius-full));
    --_remove-glyph: 0.75rem;

    display: inline-flex;
    vertical-align: middle;
    max-inline-size: 100%;
  }

  :host([size='sm']) {
    --_padding-inline: var(--padding-inline, 0.375rem);
    --_gap: 0.125rem;
  }

  :host([size='lg']) {
    --_height: var(--height, 1.75rem);
    --_font-size: var(--font-size, var(--l-text-sm));
    --_padding-inline: var(--padding-inline, 0.625rem);
    --_remove-glyph: 0.875rem;
  }

  /* Disabled is conveyed by the greyed × button + cursor, NOT by washing out
   * the label — dimming the whole chip with opacity composites the text below
   * the WCAG AA contrast floor (axe flags it; a disabled <button> is exempt but
   * the label span is not). */
  :host([disabled]) .base {
    cursor: not-allowed;
  }

  /*
  ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
   🅲🅷🅸🅿
  ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
  */

  .base {
    display: inline-flex;
    align-items: center;
    gap: var(--_gap);
    min-block-size: var(--_height);
    padding-inline: var(--_padding-inline);
    border: 1px solid var(--_border-color);
    border-radius: var(--_border-radius);
    background-color: var(--_background);
    color: var(--_color);
    font-family: var(--l-font-neo-grotesque);
    font-size: var(--_font-size);
    font-weight: var(--l-font-weight-medium);
    line-height: 1;
    min-inline-size: 0;
    transition:
      background-color 120ms ease,
      border-color 120ms ease;
  }

  /* The remove segment is exactly as tall as the chip, so the chip is where the
     target floor has to live: `--height` still tunes a removable chip, but not
     under the 24px minimum (WCAG 2.5.8). Without this the promise held only for
     the default value. */
  :host([removable]) .base {
    min-block-size: max(1.5rem, var(--_height));
  }

  /* Whole-chip hover — only on a chip that is *wholly* a control. A display chip
     has no target at all, and a removable one has exactly its × segment: tinting
     the label under the pointer there promises a click that does nothing, and on
     a filter bar it fights the segment for the eye. `[selectable]` also covers
     `control="checkbox"`, which derives the attribute. */
  @media (hover: hover) {
    :host([selectable]:hover:not([disabled])) .base {
      background-color: color-mix(in oklab, var(--_background), currentColor 7%);
      border-color: var(--_border-color-hover);
    }
  }

  .content {
    display: inline-flex;
    align-items: center;
    min-inline-size: 0;
    /* `.base` sets `line-height: 1` so the chip's height comes from its own size
       tokens rather than the document's leading. That makes the line box exactly
       one em tall, and a plain `overflow: hidden` then shears every descender
       (g, j, p, q, y) at all three sizes — measured 1px short.

       Fixed by widening the clip rectangle, not the box: growing the line box
       instead (`line-height: normal`) moves the text half a pixel against the ×
       and the prefix icon, since the font's ascent and descent are not
       symmetrical. `clip` keeps the geometry identical and simply paints the
       tails. The margin applies to every side — there are no per-axis longhands
       in any engine yet — but 0.2em of horizontal bleed is invisible next to a
       label that is hard-cut at the edge anyway. */
    overflow: clip;
    overflow-clip-margin: 0.2em;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ::slotted([slot='prefix']) {
    display: inline-flex;
    flex: none;
  }

  /* A count or trailing glyph. It sits in its own slot so `.base`'s gap gives it
     a gutter by construction — the label is a bare text node and can never be
     targeted from the outside. Lighter weight than the medium label, and
     tabular so a column of counts stays aligned. */
  ::slotted([slot='suffix']) {
    flex: none;
    font-weight: var(--l-font-weight-normal);
    font-variant-numeric: tabular-nums;
  }

  /*
  ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
   🆂🅴🅻🅴🅲🆃🅰🅱🅻🅴  (filter chip — toggle button, or a checkbox riding inside)
  ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
  */

  :host([selectable]) {
    /* Taller than a display chip at the same `size`: a chip you click is a
       target (WCAG 2.5.8), and this is the dense step between `sm` and `lg` —
       more room, same 12px label, where `lg` would jump the label to body size.
       `--height` / `--font-size` tune it from there. */
    --_height: var(--height, 1.75rem);

    /* The selection accent is the same one the library's checkbox, radio and
       switch use, so a chip with a checkbox inside reads as a single control. */
    --_accent: var(--l-form-control-activated-color);
    /* The label is 12px, so it owes 4.5:1 over its own tint — and the raw accent
       misses it on the light one (4.35:1 measured). Darken it there, lighten it
       on the dark tint, and both modes clear 6:1. `color-mix()` resolves
       reliably inside `light-dark()` — `oklch(from …)` does not. */
    --_selected-color: var(
      --selected-text-color,
      light-dark(
        color-mix(in oklab, var(--_accent), black 14%),
        color-mix(in oklab, var(--_accent), white 45%)
      )
    );
    /* Derived from the text color, so overriding `--selected-text-color` alone
       repaints the whole chip coherently. Kept at 10%: a consumer accent skips
       the mode correction above, so the tint has to stay pale enough for a
       semantic token (`--l-color-text-success` and friends) to clear 4.5:1 on
       it unaided. The border carries the rest of the emphasis. */
    --_selected-background: var(
      --selected-background-color,
      color-mix(in oklab, var(--_selected-color) 10%, var(--l-color-surface))
    );
    /* The third member of the trio, beside `--selected-text-color` and
       `--selected-background-color`. Selection already replaces the fill and the ink
       outright, so the line has to be replaceable the same way — without this,
       a chip that sets `--border-color` keeps that exact line when picked and
       the emphasis step that says "selected" never happens. 45% by default, the
       strength the derived line has always used here. */
    --_selected-border-color: var(
      --selected-border-color,
      color-mix(in oklab, var(--_selected-color) 45%, transparent)
    );
  }

  :host([selectable][size='lg']) {
    --_height: var(--height, 2rem);
  }

  :host([selectable][selected]) {
    --_background: var(--_selected-background);
    --_color: var(--_selected-color);
    --_border-color: var(--_selected-border-color);
    /* Same +12 step the resting line takes on hover, and pinned in the same way
       when the consumer supplies the value. */
    --_border-color-hover: var(
      --selected-border-color,
      color-mix(in oklab, var(--_selected-color) 57%, transparent)
    );
  }

  :host([selectable]) .base {
    cursor: pointer;
    /* A chip is tapped, not read: never leave a text selection behind a
       double-click on a filter. */
    user-select: none;
  }

  :host([selectable][disabled]) .base {
    cursor: not-allowed;
  }

  /* The chip *is* the button. Negative inline margins let it cover `.base`'s
     padding so the whole pill is the target, while `.base` keeps owning the
     border, background, and the × button's spacing math. */
  .toggle {
    flex: 1 1 auto;
    display: inline-flex;
    align-items: center;
    align-self: stretch;
    gap: var(--_gap);
    min-inline-size: 0;
    margin-inline: calc(var(--_padding-inline) * -1);
    padding-inline: var(--_padding-inline);
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: start;
    cursor: inherit;
  }

  /* Stop before the × instead of running under it. */
  :host([removable]) .toggle {
    margin-inline-end: 0;
    padding-inline-end: 0;
  }

  /* One ring for the whole chip: the inner control is a detail of how the chip
     is operated, not a second thing to focus visually — and the ring drawn
     below fully encloses the box, so the indicator only grows (WCAG 2.4.7 /
     2.4.13).

     Qualified by `:host(…)` on purpose. The shared appearance nests
     `&:focus-visible` under a selector list, so it lands as
     `:is(.l-checkbox, … input[type='checkbox']:not(…)):focus-visible` — 0,3,1,
     which outranks a bare `.checkbox:focus-visible` (0,2,0) and paints a second
     ring around the box. `:host([selectable])` brings this to 0,4,0. */
  :host([selectable]) .toggle:focus-visible,
  :host([selectable]) .checkbox:focus-visible {
    outline: none;
  }

  .base:has(:is(.toggle, .checkbox):focus-visible) {
    outline: 2px solid var(--l-focus-ring);
    outline-offset: 2px;
  }

  /* The skin comes from the shared `.l-checkbox` appearance, applied through
     `cls('checkbox')` rather than a static class literal: `dist` ships that CSS
     as a real file, so a consumer's PostCSS rewrites its selector under a
     renamed `cssPrefix` — a baked `l-checkbox` in the template would stop
     matching it. This rule only carries what is specific to a chip.

     The shared appearance sizes the box in `em`, and a UA gives `<input>` its
     own font — inheriting the chip's makes the box track the `size` step. The
     accent follows the selected color, so a themed chip keeps its checkbox in
     the same hue. */
  .checkbox {
    --accent: var(--selected-text-color, var(--l-form-control-activated-color));
    /* Hover halo — same ratio and same token flip as the light-DOM
       `.l-checkbox`, which keeps this rule out of the shared partial (see
       `css/elements/checkbox.css`). Repeated here rather than moved into the
       partial: `l-tree-item` shares that file and never opted into a glow. */
    --_halo: var(--l-form-control-border-color-hover);

    flex: none;
    font: inherit;
    transition-property: background-color, border-color, box-shadow;
  }

  .checkbox:checked,
  .checkbox:indeterminate {
    --_halo: var(--accent);
  }

  /* Driven by the chip, not by the box: the chip is the label and the whole
     click target, so a halo lighting up only over 15px of a 28px pill would
     read as a glitch. */
  @media (hover: hover) {
    :host([selectable]:not([disabled])) .base:hover .checkbox:not(:disabled) {
      box-shadow: 0 0 0 calc(var(--size) * var(--l-form-control-toggle-halo-ratio))
        color-mix(in oklab, var(--_halo), transparent 84%);
    }
  }

  /* Forced colors drop the tint (backgrounds are forced to Canvas), so pin the
     border to the system highlight — otherwise selection becomes invisible. */
  @media (forced-colors: active) {
    :host([selectable][selected]) .base {
      border-color: Highlight;
    }
  }

  /*
  ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
   🆁🅴🅼🅾🆅🅴  (≥24px target-size, mdi:close mask — mirrors l-close)
  ▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬
  */

  .remove {
    flex: none;
    display: grid;
    place-items: center;
    /* Full height, and split off by a rule: the affordance is drawn *at rest*
       rather than conjured on hover. Keyboard and touch users never get a hover
       state, and a filter bar of chips whose delete zone only appears under the
       pointer is a row of faint marks until you go hunting. */
    align-self: stretch;
    /* Square, and never under the 24px hit target (WCAG 2.5.8) however a
       consumer tunes `--height`. */
    inline-size: max(1.5rem, var(--_height));
    /* The segment spans `.base`'s *border* box, not its content box: the −1px
       block margins cancel the border on each side (a stretched flex item is
       sized by the line minus its own margins), which both lands the target on
       a full 24px — 22px otherwise, one short — and runs the rule edge to edge
       so it meets the chip's own border instead of stopping just inside it. */
    margin-block: -1px;
    /* End: flush to the chip's inner edge. Start: pushed off the label so it
       gets the same air on both sides. `.base`'s `gap` spaces *slotted items*
       (icon ↔ label) and is deliberately tighter than the chip's padding, so
       letting it also front the rule left the label with 8px on its left and
       4px on its right — visibly crammed against the divider. This tops the gap
       back up to a full `--_padding-inline`, at every size. */
    margin-inline: calc(var(--_padding-inline) - var(--_gap))
      calc(var(--_padding-inline) * -1 - 1px);
    padding: 0;
    /* Transparent on three sides, so `background-clip` below keeps the hover
       fill off the 1px the chip's own border occupies. */
    border: 1px solid transparent;
    border-inline-start-color: var(--_border-color);
    /* The chip's own curve — the padding box rounds to one pixel less on its
       own, so the fill stops on the arc rather than squaring off the pill. */
    border-start-end-radius: var(--_border-radius);
    border-end-end-radius: var(--_border-radius);
    background-color: transparent;
    background-clip: padding-box;
    color: inherit;
    cursor: pointer;
    transition: background-color 120ms ease;
    /* The press dip goes through a variable so the reduced-motion block can
       cancel it by re-declaring *this* one line. Pairing the two `:active`
       selectors instead is a trap: the override only wins on source order while
       the specificities tie, and adding the `:not(:disabled)` guard below was
       enough to silently break it. */
    --_press-scale: 0.86;

    &::after {
      content: '';
      inline-size: var(--_remove-glyph);
      block-size: var(--_remove-glyph);
      background-color: currentColor;
      transition: scale 120ms ease;
      /* https://pictogrammers.com/library/mdi/icon/close */
      mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" /></svg>')
        center / contain no-repeat;
    }

    /* The whole segment fills. The chip already tints under the pointer (7%),
       so this has to read as the stronger of the two — it is what will actually
       be clicked. */
    @media (hover: hover) {
      &:hover:not(:disabled) {
        background-color: color-mix(in oklab, transparent, currentColor 12%);
      }
    }

    &:active:not(:disabled) {
      background-color: color-mix(in oklab, transparent, currentColor 22%);
    }

    /* The glyph dips, not the segment: scaling the box would shrink the rule
       and pull it away from the chip's border. */
    &:active:not(:disabled)::after {
      scale: var(--_press-scale);
    }

    /* Inset, unlike the library's usual +2px: the segment is flush with the
       chip's border on three sides, so an outset ring would sit half outside
       the chip and clip against its neighbour in a wrapped filter bar. */
    &:focus-visible {
      outline: 2px solid var(--l-focus-ring);
      outline-offset: -2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.4;
    }

    /* `background-color` is forced to Canvas in high contrast, which would erase
       a masked glyph entirely — repaint it in the system button color. */
    @media (forced-colors: active) {
      &::after {
        background-color: ButtonText;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .base,
    .checkbox,
    .remove,
    .remove::after {
      transition-duration: 0ms;
    }
    .remove {
      --_press-scale: 1;
    }
  }
}
